import { Button, SubmitButton } from "@dealer-center/core-components";
import { NewRelic } from "@dealer-center/core-third-party-newrelic";
import { useEffect, useState } from "react";
import styled from "styled-components";

interface SaveAndCancelInterface {
  onSave: () => void;
  onCancel: () => void;
  isDisabled?: boolean | { save?: boolean; cancel?: boolean };
  saveButtonText?: string;
  isSaving?: boolean;
  testId?: string;
  savingText?: string;
  className?: string;
  cancelButtonText?: string;
  /**
   * @param analyticsEventName - The name of the event to be sent to GTM
   * @example
   *  analyticsEventName: "Website Inventory Config Import Configuration"
   */
  analyticsEventName: string;
  children?: React.ReactNode | React.ReactNode[];
}

const StyledSaveAndCancel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;
export const SaveAndCancel = ({
  onSave,
  onCancel,
  isDisabled,
  saveButtonText = "Save",
  cancelButtonText = "Cancel",
  analyticsEventName,
  className,
  isSaving = false,
  savingText = "Saving...",
  testId = analyticsEventName.replace(/ /g, "-").toLowerCase(),
  children
}: SaveAndCancelInterface) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  useEffect(() => {
    const saveStarted = () => {
      setStartTime(new Date());
    };
    const saveFinished = () => {
      if (!startTime) {
        return;
      }
      const endTime = new Date();
      const duration = endTime.getTime() - startTime.getTime();
      setStartTime(null);
      NewRelic.addPageAction("Submit Finished", {
        Duration: duration,
        Action: analyticsEventName
      });
    };

    if (isSaving && !startTime) {
      saveStarted();
    } else if (!isSaving && startTime) {
      saveFinished();
    }
    return () => {
      if (startTime) {
        saveFinished();
      }
    };
  }, [analyticsEventName, isSaving, startTime]);
  const isCancelDisabled =
    typeof isDisabled === "object" ? isDisabled.cancel : isDisabled;
  const isSaveDisabled =
    typeof isDisabled === "object" ? isDisabled.save : isDisabled;
  return (
    <StyledSaveAndCancel className={className}>
      {children}
      <Button
        analyticsConfiguration={{
          eventName: `${analyticsEventName} cancel button clicked`
        }}
        buttonStyle="secondary"
        className="save-and-cancel__cancel"
        data-testid={`${testId}-cancel-button`}
        disabled={isCancelDisabled || isSaving}
        sx={{
          marginRight: "1rem"
        }}
        onClick={onCancel}
      >
        {cancelButtonText}
      </Button>
      <SubmitButton
        analyticsConfiguration={{
          eventName: `${analyticsEventName} save button clicked`
        }}
        buttonStyle="primary"
        className="save-and-cancel__save"
        data-testid={`${testId}-save-button`}
        disabled={isSaveDisabled}
        isLoading={isSaving}
        onClick={onSave}
      >
        {saveButtonText}
      </SubmitButton>
    </StyledSaveAndCancel>
  );
};
